import React, { createContext, useContext, useEffect, useMemo } from "react";
import eventTracker from "@rapid/analytics/EventTracker";
import { AppSource } from "@rapid/analytics/types";

export enum RapidEmbedSource {
    App = "app",
    Universe = "universe",
    Admin = "admin"
}

export enum RapidEmbedSurface {
    Default = "default",
    SolutionsInlineArtifact = "solutions-inline-artifact"
}

export interface EmbeddedInfo {
    isEmbedded: boolean;
    source: RapidEmbedSource | null;
    surface: RapidEmbedSurface;
    appSource: AppSource;
}

const defaultInfo: EmbeddedInfo = {
    isEmbedded: false,
    source: null,
    surface: RapidEmbedSurface.Default,
    appSource: AppSource.RAPID
};

const EmbeddedContext = createContext<EmbeddedInfo>(defaultInfo);

export function EmbeddedProvider({
    source,
    surface,
    children
}: {
    source?: RapidEmbedSource;
    surface?: RapidEmbedSurface;
    children: React.ReactNode;
}) {
    const parentInfo = useContext(EmbeddedContext);
    const info = useMemo<EmbeddedInfo>(() => {
        const nextSource = source ?? parentInfo.source;
        const nextSurface = surface ?? parentInfo.surface;

        if (!nextSource) {
            return { ...defaultInfo, surface: nextSurface };
        }

        return {
            isEmbedded: true,
            source: nextSource,
            surface: nextSurface,
            appSource: `rapid:embedded:${nextSource}` as AppSource
        };
    }, [parentInfo.source, parentInfo.surface, source, surface]);

    useEffect(() => {
        eventTracker.setAppSource(info.appSource);
    }, [info.appSource]);

    return <EmbeddedContext.Provider value={info}>{children}</EmbeddedContext.Provider>;
}

export function useEmbedded(): EmbeddedInfo {
    return useContext(EmbeddedContext);
}

export function useIsEmbedded(): boolean {
    return useContext(EmbeddedContext).isEmbedded;
}

export function useIsInlineEmbeddedRapid(): boolean {
    const { isEmbedded, surface } = useContext(EmbeddedContext);
    return isEmbedded && surface === RapidEmbedSurface.SolutionsInlineArtifact;
}
